import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CryptoProvider } from './contexts/CryptoContext';
import { MKGuard, useLockOnLogout } from './components/AppRouter';
import Navigation from './components/Navigation';
import Homepage from './pages/Homepage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import VaultPage from './pages/VaultPage';
import BeneficiariesPage from './pages/BeneficiariesPage';
import CheckinPage from './pages/CheckinPage';
import SettingsPage from './pages/SettingsPage';
import CGUPage from './pages/CGUPage';
import ConfidentialitePage from './pages/ConfidentialitePage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import FAQPage from './pages/FAQPage';
import PricingPage from './pages/PricingPage';
import OnboardingPage from './pages/OnboardingPage';
import AddBeneficiaryPage from './pages/AddBeneficiaryPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AuthCallback from './pages/AuthCallback';
import ConfirmBeneficiaryPage from './pages/ConfirmBeneficiaryPage';
import TransmissionPage from './pages/TransmissionPage';
import AccountDeletedPage from './pages/AccountDeletedPage';

const PUBLIC_PATHS = ['/', '/login', '/register', '/verify-email', '/auth/callback', '/onboarding', '/legal/cgu', '/legal/confidentialite', '/legal/mentions-legales', '/faq', '/pricing', '/confirm-beneficiary', '/transmission', '/compte-supprime'];

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A1628', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#00F5A0', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#8892A4', fontSize: 14 }}>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (user && !user.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  if (user && user.onboarding_completed && location.pathname === '/onboarding') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <MKGuard>
      {children}
    </MKGuard>
  );
};

const HomeRoute = () => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return null;
  if (isAuthenticated && user) return <Navigate to={user.onboarding_completed ? '/dashboard' : '/onboarding'} replace />;
  return <Homepage />;
};

function AppContent() {
  useLockOnLogout();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const showNav = isAuthenticated && !PUBLIC_PATHS.includes(location.pathname);

  return (
    <>
      {showNav && <Navigation />}
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/vault" element={
          <ProtectedRoute>
            <VaultPage />
          </ProtectedRoute>
        } />
        <Route path="/beneficiaries" element={
          <ProtectedRoute>
            <BeneficiariesPage />
          </ProtectedRoute>
        } />
        <Route path="/beneficiaries/add" element={
          <ProtectedRoute>
            <AddBeneficiaryPage />
          </ProtectedRoute>
        } />
        <Route path="/checkin" element={
          <ProtectedRoute>
            <CheckinPage />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        } />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/legal/cgu" element={<CGUPage />} />
        <Route path="/legal/confidentialite" element={<ConfidentialitePage />} />
        <Route path="/legal/mentions-legales" element={<MentionsLegalesPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/confirm-beneficiary" element={<ConfirmBeneficiaryPage />} />
        <Route path="/transmission" element={<TransmissionPage />} />
        <Route path="/compte-supprime" element={<AccountDeletedPage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <CryptoProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </CryptoProvider>
  );
}

export default App;
